import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Icon from '../../../components/AppIcon';

const ReferralDataTable = ({ referrals, sortConfig, onSort }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: 'Nouveau', color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
      reviewed: { label: 'En revue', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' },
      scheduled: { label: 'Programmé', color: 'bg-green-500/20 text-green-400 border-green-500/40' },
      completed: { label: 'Terminé', color: 'bg-gray-500/20 text-gray-400 border-gray-500/40' },
      cancelled: { label: 'Annulé', color: 'bg-red-500/20 text-red-400 border-red-500/40' }
    };

    const config = statusConfig?.[status] || statusConfig?.new;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getSortIcon = (field) => {
    if (sortConfig?.field !== field) {
      return <Icon name="ChevronsUpDown" size={16} className="text-gray-500" />;
    }
    return sortConfig?.direction === 'asc' ? (
      <Icon name="ChevronUp" size={16} className="text-blue-400" />
    ) : (
      <Icon name="ChevronDown" size={16} className="text-blue-400" />
    );
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: fr });
    } catch {
      return '-';
    }
  };

  const getPatientInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}`;
  };

  if (referrals?.length === 0) {
    return (
      <div className="bg-[rgba(26,26,30,0.95)] backdrop-blur-2xl border border-white/10 p-12 rounded-2xl text-center shadow-2xl">
        <Icon name="Inbox" size={48} className="text-gray-500 mx-auto mb-4" />
        <p className="text-lg text-gray-300">Aucun adressage trouvé</p>
        <p className="text-sm text-gray-400 mt-2">Essayez de modifier vos filtres</p>
      </div>
    );
  }

  return (
    <div className="bg-[rgba(26,26,30,0.95)] backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
      <div className="px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Icon name="Table" size={20} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Données d'Adressage</h3>
            <p className="text-sm text-gray-400">{referrals?.length} résultat{referrals?.length > 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[rgba(18,18,22,0.95)] border-b border-white/10">
            <tr>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => onSort('submissionDate')}
                  className="flex items-center gap-2 text-sm font-semibold text-white hover:text-blue-400 transition-colors"
                >
                  Date
                  {getSortIcon('submissionDate')}
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <span className="text-sm font-semibold text-white">Patient</span>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => onSort('referringDentistName')}
                  className="flex items-center gap-2 text-sm font-semibold text-white hover:text-blue-400 transition-colors"
                >
                  Dentiste
                  {getSortIcon('referringDentistName')}
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <span className="text-sm font-semibold text-white">Dents</span>
              </th>
              <th className="px-6 py-4 text-left">
                <span className="text-sm font-semibold text-white">Motifs</span>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center gap-2 text-sm font-semibold text-white hover:text-blue-400 transition-colors"
                >
                  Statut
                  {getSortIcon('status')}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {referrals?.map((referral) => (
              <tr
                key={referral?.id}
                className="hover:bg-white/5 transition-colors"
              >
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-300">
                    {formatDate(referral?.submissionDate)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <span className="text-xs font-semibold text-blue-400">
                        {getPatientInitials(referral?.patientFirstName, referral?.patientLastName)}
                      </span>
                    </div>
                    <span className="text-sm text-gray-300">
                      {getPatientInitials(referral?.patientFirstName, referral?.patientLastName)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="text-sm font-medium text-gray-200">
                      {referral?.referringDentistName || '-'}
                    </p>
                    {referral?.referringPracticeName && (
                      <p className="text-xs text-gray-400">
                        {referral?.referringPracticeName}
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {referral?.toothNumbers?.slice(0, 3)?.map((tooth, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20"
                      >
                        {tooth}
                      </span>
                    ))}
                    {referral?.toothNumbers?.length > 3 && (
                      <span className="text-xs text-gray-400">
                        +{referral?.toothNumbers?.length - 3}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="max-w-xs">
                    <p className="text-sm text-gray-300 truncate">
                      {referral?.referralReasons?.[0] || '-'}
                    </p>
                    {referral?.referralReasons?.length > 1 && (
                      <p className="text-xs text-gray-400">
                        +{referral?.referralReasons?.length - 1} autre{referral?.referralReasons?.length > 2 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(referral?.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReferralDataTable;